import { getDocumentReferenceForBinaryId } from '@ctw/shared/api/fhir/binaries';
import type { DocumentModel } from '@ctw/shared/api/fhir/models/document';
import type { EncounterModel } from '@ctw/shared/api/fhir/models/encounter';
import { ContentCard } from '@ctw/shared/components/containers/content-card';
import { DefinitionList } from '@ctw/shared/components/definition-list';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { ResponsiveSourceDocumentLink } from '@ctw/shared/content/CCDA/responsive-source-document-link';
import { RelatedDocumentsList } from '@ctw/shared/content/documents/related-documents-list';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import type { UseResourceDrawerOpenDrawerArgs } from '@ctw/shared/context/resource-drawer-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { tw } from '@ctw/shared/utils/tailwind';
import { capitalize } from 'lodash-es';
import { useEffect, useState } from 'react';

export const encounterDrawerAction: UseResourceDrawerOpenDrawerArgs<'Encounter'>['action'] = ({
  model,
}) => {
  const upid = model.patientUPID ?? model.patient?.patientUPID;
  const shouldShowSourceDocumentLink =
    upid !== undefined && upid !== '' && window.location.pathname.includes(upid);

  return {
    label: 'Encounter',
    title: model.title,
    content: {
      body: (
        <div className={tw`flex flex-col gap-5`}>
          <ContentCard
            variant="focal"
            title="Details"
            content={{
              header: shouldShowSourceDocumentLink && (
                <ResponsiveSourceDocumentLink model={model} />
              ),
            }}
          >
            <DefinitionList
              items={[
                { label: 'Status', value: capitalize(model.status) },
                { label: 'Class', value: capitalize(model.classDisplay) },
                { label: 'Type', value: capitalize(model.typeDisplay) },
                { label: 'Location', value: model.location.join(', ') },
                {
                  label: 'Providers',
                  value: model.practitioners.length > 0 && (
                    <ul className={tw`m-0 list-disc pl-4`}>
                      {model.practitioners.map((p) => (
                        <li key={p}>{p}</li>
                      ))}
                    </ul>
                  ),
                },
                {
                  label: 'Location Type',
                  value: (() => {
                    const locationType = model.locationType.filter(
                      (value) => value.toLowerCase() !== 'unknown',
                    );
                    if (locationType.length > 0) {
                      return (
                        <ul className={tw`m-0 list-disc pl-4`}>
                          {locationType
                            .filter((value) => value.toLowerCase() !== 'unknown')
                            .map((s) => (
                              <li key={s}>{s}</li>
                            ))}
                        </ul>
                      );
                    }
                    return null;
                  })(),
                },
                { label: 'Reason', value: model.reason },
                {
                  label: 'Diagnoses',
                  value: model.diagnoses.length > 0 && (
                    <ul className={tw`m-0 list-disc pl-4`}>
                      {model.diagnoses.map((d) => (
                        <li key={d}>{d}</li>
                      ))}
                    </ul>
                  ),
                },
                { label: 'Discharge Disposition', value: model.dischargeDisposition },
              ]}
            />
          </ContentCard>
          <EncounterDocumentsSection model={model} />
        </div>
      ),
    },
  };
};

interface EncounterDocumentsSectionProps {
  model: EncounterModel;
}

const EncounterDocumentsSection = ({ model }: EncounterDocumentsSectionProps) => {
  const useRelatedBinaries = useFeatureFlag('ctw-encounter-notes-use-related-binaries');

  if (useRelatedBinaries) {
    return <EncounterSourceDocuments model={model} />;
  }

  return (
    <RelatedDocumentsList
      isLoading={model.isLoadingClinicalNotes}
      items={model.docsAndNotes.map((document) => ({ preferred: false, document }))}
    />
  );
};

interface EncounterSourceDocumentsProps {
  model: EncounterModel;
}

const EncounterSourceDocuments = ({ model }: EncounterSourceDocumentsProps) => {
  const telemetry = useTelemetry();
  const { graphqlClient } = useCTW();
  const hasShowSuspectedDuplicateDocumentsFeature = useFeatureFlag(
    'ctw-show-suspected-duplicate-documents',
  );

  const [isLoading, setIsLoading] = useState(model.binaryIds.length > 0);
  const [documents, setDocuments] = useState<Array<DocumentModel>>([]);

  useEffect(() => {
    let cancelled = false;

    if (model.binaryIds.length > 0) {
      setIsLoading(true);
      setDocuments([]);
      const upid = model.patientUPID ?? model.patient?.patientUPID ?? '';
      Promise.allSettled(
        model.binaryIds.map((binaryId) =>
          getDocumentReferenceForBinaryId({
            upid,
            binaryId,
            graphqlClient,
            telemetry,
            excludeSuspectedDuplicates: !hasShowSuspectedDuplicateDocumentsFeature,
          }),
        ),
      ).then((results) => {
        if (cancelled) {
          return;
        }
        const resolved = results
          .map((r) => (r.status === 'fulfilled' ? r.value : undefined))
          .filter((d): d is DocumentModel => d !== null && d !== undefined);
        setDocuments(resolved);
        setIsLoading(false);
      });
    }

    return () => {
      cancelled = true;
    };
  }, [model, hasShowSuspectedDuplicateDocumentsFeature, graphqlClient, telemetry]);

  if (model.binaryIds.length === 0) {
    return null;
  }

  if (isLoading) {
    return (
      <div className={tw`flex w-full justify-center`}>
        <LoadingSpinner className={tw`text-content-icon`} message="Loading source documents" />
      </div>
    );
  }

  if (documents.length === 0) {
    return null;
  }

  return (
    <div className={tw`space-y-2`}>
      <div className={tw`font-semibold text-lg`}>Source Documents</div>
      <ul className={tw`m-0 list-none space-y-1 p-0`}>
        {documents.map((document) => (
          <li
            key={document.id}
            className={tw`flex items-center justify-between gap-2 rounded-lg border bg-white p-3`}
          >
            <span className={tw`min-w-0 flex-1 break-words font-medium text-background-inverse`}>
              {document.title}
            </span>
            <ResponsiveSourceDocumentLink model={document} binaryId={document.binaryId} />
          </li>
        ))}
      </ul>
    </div>
  );
};
